import { UserData } from '@buildwise/pages/admin/userAdministration/CompanyUsers';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import { UserType } from 'hooks/useUserData';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { useUserForm } from '../../hooks/useUserForm';
import ProfileAvatar from '@buildwise/libs/people/components/Details/ProfileAvatar';
import UserDefaultFields from './UserDefaultFields';
import GrowDialogTransition from 'components/modal/GrowDialogTransition';
import UserCustomFields from './UserCustomFields';
import UserOtherFields from './UserOtherFields';

export type ModifyUserFormData = {
  canAddProjects: boolean;
  firstname: string;
  lastname: string;
  email: string;
  title: string;
  primaryCompanyName: string;
  role: string;
  active: string;
  canEndorseFlag: boolean;
  canSuggestRevisiontToPostsFlag: boolean;
  canMovePostsToTopFlag: boolean;
  canCreatePlaylistFlag: boolean;
  canInviteOthersFlag: boolean;
};

type EditUserModalTypes = {
  open: boolean;
  people: UserType[];
  user: UserData;
  setOpen: Dispatch<SetStateAction<boolean>>;
  companyId?: string;
};

function EditUserModal({ open, people, user, setOpen, companyId }: EditUserModalTypes) {
  const editUser = useMemo(() => {
    return people.find(
      (userData) =>
        userData?.username === user?.userName && userData?.companyId === user?.companyId,
    );
  }, [people, user]);

  const {
    customInputFields,
    isCustomFieldsFetched,
    isSubmitting,
    profileForm,
    closeModal,
    onSubmit,
    isDirty,
  } = useUserForm({ user, editUser, setOpen, companyId });

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      TransitionComponent={GrowDialogTransition}
    >
      <CustomDialogTitle id="dialog-title" {...{ onClose: closeModal }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: 'black' }}>
          Modify User
        </Typography>
      </CustomDialogTitle>
      <DialogContent dividers id="dialog-description">
        <form onSubmit={onSubmit}>
          <FormProvider {...profileForm}>
            {editUser && (
              <Box
                mb={3}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <ProfileAvatar user={editUser} isLoggedInUser={false} />
                {editUser.userNameToDisplay && <UserName>{editUser.userNameToDisplay}!</UserName>}
              </Box>
            )}
            <UserDefaultFields />
            <Collapse in={isCustomFieldsFetched}>
              <UserCustomFields fields={customInputFields} />
            </Collapse>
            <UserOtherFields />
          </FormProvider>
        </form>
      </DialogContent>
      <DialogActions>
        <Button type="submit" disabled={isSubmitting} onClick={onSubmit} variant="contained">
          {isSubmitting ? (
            <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
          ) : (
            'Submit'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const UserName = styled(Typography)(({ theme }) => ({
  marginTop: 8,
  color: theme.palette.custom.tuna,
  fontSize: 22,
  fontWeight: 700,
}));

export default EditUserModal;
